import {
	OrdersListView,
	ProductDirection,
	INTEGRATED_PRODUCTS,
	PRODUCTS_RESEND_SMS,
	PRODUCTS_RESEND_EMAIL,
	ProductShortData,
	OrderModel,
	ProductDirectionVbv,
	ProductStatus,
	MerchantWithProducts,
} from 'Pages/orders_design'
import { OrderProductModel } from 'Pages/orders_design/models/orderProduct/OrderProduct'
import { getDate, getTime, isNotProdServer } from 'Helpers'
import { ProductSlugs } from 'Src/constants/ProductSlugs'
import { MODAL_NAME } from 'Src/constants/ModalName'
import { OutLinks } from 'Src/constants/OutLinks'
import { FieldNames } from 'Src/constants/FieldNames'
import { Callback } from 'Types/utilTypes'
import { IframeComponent } from 'LibNeo'
import moment from 'moment/moment'
import { DATE_FORMAT } from 'Constants/Global'

export const listViewSelector = (currentView: OrdersListView): OrdersListView =>
	currentView === OrdersListView.FULL ? OrdersListView.COLLAPSED : OrdersListView.FULL

export const isOnlineProduct = (product: OrderProductModel): boolean =>
	product.direction === ProductDirection.ONLINE

export const hasOnlineProduct = (products: OrderProductModel[]): boolean =>
	products.some(isOnlineProduct)

export const hasProductsSlug = (slugs: string[], products: OrderProductModel[]): boolean =>
	products.some((item): boolean => slugs.includes(item.product.slug))

export const getMobileProduct = (products: OrderProductModel[]): OrderProductModel =>
	products.find((product): boolean => Boolean(product.sber_telecom_auth_url))

export const hasIntegrationProduct = (products: OrderProductModel[]): boolean =>
	hasProductsSlug(INTEGRATED_PRODUCTS, products) || Boolean(getMobileProduct(products))

export const getIntegrationProduct = (products: OrderProductModel[]): OrderProductModel[] =>
	products.filter(
		(product): boolean =>
			INTEGRATED_PRODUCTS.includes(product.product.slug) || Boolean(product.sber_telecom_auth_url)
	)

export const getSmsSendProduct = (products: OrderProductModel[]): OrderProductModel[] =>
	products.filter((product): boolean => PRODUCTS_RESEND_SMS.includes(product.product.slug))

export const getEmailSendProduct = (products: OrderProductModel[]): OrderProductModel[] =>
	products.filter((product): boolean => PRODUCTS_RESEND_EMAIL.includes(product.product.slug))

export const getDateTime = (date: string, divider = ' '): string =>
	date ? `${getDate(date)}${divider}${getTime(date)}` : null

export const getSortedIntegratedProducts = (products: OrderProductModel[]): ProductShortData[] => {
	const integratedProducts: ProductShortData[] = []
	INTEGRATED_PRODUCTS.forEach((slug) => {
		const candidate = products.find(({ product }) => product.slug === slug)
		if (candidate) {
			integratedProducts.push(candidate.product)
		}
	})
	return integratedProducts
}

export const createIntegratedProductsModalConfig = (
	key: ProductSlugs,
	inn?: string
): { titleCode: string; modalName: MODAL_NAME; url: string } => {
	let urlSberegiBiznes = isNotProdServer()
		? OutLinks.product_sberegi_biznes_test
		: OutLinks.product_sberegi_biznes
	urlSberegiBiznes = urlSberegiBiznes.replace(':inn', inn)
	switch (key) {
		case ProductSlugs.DOCDOC:
			return {
				titleCode: 'order.title_doc_doc',
				modalName: MODAL_NAME.DOC_DOC,
				url: OutLinks.product_doc_doc,
			}
		case ProductSlugs.PARTNER_VERIFICATION_SERVICE:
			return {
				titleCode: 'order.title_spk',
				modalName: MODAL_NAME.SPK,
				url: OutLinks.verification_service,
			}
		case ProductSlugs.SBEREGI_BIZNES:
			return {
				titleCode: 'order.title_sberegi_biznes',
				modalName: MODAL_NAME.SBEREGI_BIZNES,
				url: urlSberegiBiznes,
			}
		case ProductSlugs.INSALES:
			return {
				titleCode: 'order.title_insales',
				modalName: MODAL_NAME.INSALES,
				url: OutLinks.insales.replace(':inn', inn),
			}
		case ProductSlugs.DOUBLE_GIS:
			return {
				titleCode: 'order.title_double_gis',
				modalName: MODAL_NAME.DOUBLE_GIS,
				url: OutLinks.product_double_gis,
			}
		// case ProductSlugs.MOYA_BUHGALTERIYA_AUTSORSING:
		// 	return {
		// 		titleCode: 'order.title_moya_buhgalteriya_autsorsing',
		// 		modalName: MODAL_NAME.MOYA_BUHGALTERIYA_AUTSORSING,
		// 		url: OutLinks.product_moya_buhgalteriya_autsorsing,
		// 	}
		default:
			return null
	}
}

export const openExternalLink = (url): Window => window.open(url)

export const getAutoCreatedData = (item: OrderModel) => {
	if (!item.autocreated) return null
	if (item?.autoorder_source)
		return {
			field: FieldNames.AUTOORDER_SOURCE,
			description: 'Регистрация бизнеса и открытие ПУ',
		}
	if (item.bg_request)
		return {
			field: FieldNames.BG_REQUEST,
			description: 'Кабинета брокера',
		}
	if (item.rbidos_request)
		return {
			field: FieldNames.RBIDOS_REQUEST,
			description: 'Кабинета регистратора',
		}
	return null
}

export const isAvailableDataInFields = (data: FieldNames[], item: OrderModel) =>
	data.every(
		(fieldName) => !item[fieldName] || (Array.isArray(item[fieldName]) && !item[fieldName].length)
	)

export const hasReserveProduct = (item: OrderModel) => {
	return item?.products?.some((product) => product.reserve_account)
}
export const hasBvbProduct = (item: OrderModel) => {
	return item?.products?.some(({ direction }) => ProductDirectionVbv.includes(direction))
}

export const hasDelayedOnlineProducts = (item: OrderModel) => {
	return item.products?.some((product) => product.direction === ProductDirection.DELAYED_ONLINE)
}

export const hasReattachedProducts = (item: OrderModel) => {
	return item.products.find((product) => product.status.slug === ProductStatus.REATTACHED)
}

export const merchantWithProducts = (item: OrderModel) => {
	return item.merchants.map(
		(merchant): MerchantWithProducts => ({
			...merchant,
			products: item.products.filter((product) => product.merchant_id === merchant.id),
		})
	)
}

export const getButtonForProductIntegration = (
	product: OrderProductModel,
	showModal: Callback,
	getIntegrationOrderAndShowModal: Callback,
	item: OrderModel
) => {
	const basePropsModal = {
		Component: IframeComponent,
		width: '70vw',
	}
	const url = isNotProdServer()
		? OutLinks.product_sberegi_biznes_test
		: OutLinks.product_sberegi_biznes

	if (product.product.slug === ProductSlugs.DOCDOC) {
		return {
			id: 1,
			name: 'Подача заявки на продукт "Сберздоровье"',
			onClick: () =>
				showModal({
					...basePropsModal,
					title: 'order.title_doc_doc',
					key: MODAL_NAME.DOC_DOC,
					componentProps: { url: OutLinks.product_doc_doc },
				}),
		}
	}
	if (product.product.slug === ProductSlugs.SBEREGI_BIZNES) {
		return {
			id: 2,
			name: 'Подача заявки на продукт "Сбереги бизнес"',
			onClick: () =>
				showModal({
					...basePropsModal,
					title: 'order.title_sberegi_biznes',
					key: MODAL_NAME.SBEREGI_BIZNES,
					componentProps: { url: url.replace(':inn', item?.inn) },
				}),
		}
	}
	if (product.product.slug === ProductSlugs.INSALES) {
		return {
			id: 3,
			name: 'Подача заявки на продукт "InSales"',
			onClick: () =>
				showModal({
					...basePropsModal,
					title: 'order.title_insales',
					key: MODAL_NAME.INSALES,
					componentProps: { url: OutLinks.insales.replace(':inn', item?.inn) },
				}),
		}
	}
	if (product.product.slug === ProductSlugs.PARTNER_VERIFICATION_SERVICE) {
		return {
			id: 4,
			name: 'Подача заявки на продукт "Сервис проверки контрагента"',
			onClick: () =>
				showModal({
					...basePropsModal,
					title: 'order.title_spk',
					key: MODAL_NAME.SPK,
					componentProps: { url: OutLinks.verification_service },
				}),
		}
	}
	if (product.product.slug === ProductSlugs.DOUBLE_GIS) {
		return {
			id: 5,
			name: 'Подача заявки на продукт "2ГИС"',
			onClick: () =>
				showModal({
					...basePropsModal,
					title: 'order.title_double_gis',
					key: MODAL_NAME.DOUBLE_GIS,
					componentProps: { url: OutLinks.product_double_gis },
				}),
		}
	}
	if (getMobileProduct([product])) {
		return {
			id: 6,
			name: 'Повторная заявка на продукт СберМобайл',
			onClick: () =>
				getIntegrationOrderAndShowModal({
					id: item.id,
					optionsModal: {
						...basePropsModal,
						title: 'order.title_mobile',
						key: MODAL_NAME.SBER_MOBILE,
					},
				}),
		}
	}
	// if (product.product.slug === ProductSlugs.MOYA_BUHGALTERIYA_AUTSORSING) {
	// 	return {
	// 		id: 7,
	// 		name: 'Подача заявки на продукт "Моя бухгалтерия аутсорсинг"',
	// 		onClick: () =>
	// 			showModal({
	// 				...basePropsModal,
	// 				title: 'order.title_moya_buhgalteriya_autsorsing',
	// 				key: MODAL_NAME.MOYA_BUHGALTERIYA_AUTSORSING,
	// 				componentProps: { url: OutLinks.product_moya_buhgalteriya_autsorsing },
	// 			}),
	// 	}
	// }
}
export const titlePartnerRegistrationDate = (data: string): string => {
	if (!data) return 'Не определена (на дату подачи заявки)'
	const date = moment(data)
	const days = moment().diff(date, 'days')
	return `${date.format(DATE_FORMAT)}${days < 32 ? ' (менее месяца назад)' : ''}`
}
